import { Menu, MenuItem, TextField } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useAppSelector } from "app/hooks";
import { inputStyles } from "components/FormControls/styles";
import { ReactComponent as ChevronDownIcon } from "components/Icons/chevron-down.svg";
import { ReactComponent as SearchSmIcon } from "components/Icons/search-sm.svg";
import { ReactComponent as CloseIcon } from "components/Icons/x-close.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useContextMenu } from "hooks/contextMenu";
import { useMessage } from "hooks/intl";
import { useDebounce } from "hooks/useDebounce";
import { kebabCase } from "lodash";
import { OLButton } from "OLButton";
import { useEffect, useState } from "react";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    textInput: inputStyles,
    inputWidth: {
      width: 340,
    },
    inputItems: {
      display: "flex",
      gap: theme.spacing(4),
      marginRight: theme.spacing(4),
    },
    dropdown: {
      display: "flex",
      alignItems: "center",
      background: theme.gray100,
      borderRadius: "4px",
    },
    button: {
      padding: theme.spacing(3, 4),
      fontSize: "12px",
      fontWeight: 500,
      lineHeight: 1,
      color: "#222",
      textTransform: "none",
      borderRadius: "4px",
    },
    dropdownIcon: {
      height: "auto",
      marginLeft: theme.spacing(2),
      transition: "0.3s",
      transform: ({ isMenuOpen }: { isMenuOpen: boolean }) =>
        `rotate(${isMenuOpen ? "180deg" : "0"})`,
      width: "10px",
    },
    searchIcon: {
      color: theme.gray500,

      "& path": {
        strokeWidth: 1.5,
      },
    },
    closeIcon: {
      minWidth: 16,
      height: 16,
      marginLeft: theme.spacing(4),
      color: theme.gray500,

      "& path": {
        strokeWidth: 1.5,
      },

      "&:hover": {
        cursor: "pointer",
      },
    },
    menu: {
      top: "8px",
      maxHeight: "280px",
    },
  }),
);

type Property = {
  id: string;
  label: string;
};

export type DynamicSearchType = {
  query: string;
  value: string;
};

interface Props {
  property: string;
  properties?: Property[];
  setDynamicSearch: ({ query, value }: DynamicSearchType) => void;
  initialInputValue?: string;
  className?: string;
  onSearch?: () => void;
  clearValueOnStoreChange?: boolean;
}

export function DynamicSearch({
  property,
  properties,
  setDynamicSearch,
  initialInputValue,
  className = "",
  onSearch,
  clearValueOnStoreChange = true,
}: Props) {
  const [selectedProperty, setSelectedProperty] = useState(
    properties?.find((availableProperty) => availableProperty.id === property),
  );
  const contextMenu = useContextMenu();
  const classes = useStyles({ isMenuOpen: contextMenu.isMenuOpen });
  const { message } = useMessage();
  const [inputValue, setInputValue] = useState(initialInputValue || "");
  const { debouncedValue, isTyping } = useDebounce(inputValue);
  const store = useAppSelector(selectCurrentStoreData);

  useEffect(() => {
    setDynamicSearch({
      query: selectedProperty ? selectedProperty.id : property,
      value: debouncedValue,
    });
  }, [debouncedValue, selectedProperty, setDynamicSearch, property]);

  useEffect(() => {
    const shouldTriggerOnSeach = debouncedValue !== inputValue;
    if (onSearch && shouldTriggerOnSeach && !isTyping) {
      onSearch();
    }
  }, [debouncedValue, inputValue, onSearch, isTyping]);

  useEffect(() => {
    if (clearValueOnStoreChange) {
      setInputValue("");
    }
  }, [store, clearValueOnStoreChange]);

  const areManyProperties = properties && properties.length > 0;

  return (
    <TextField
      className={`${classes.textInput} ${classes.inputWidth} ${className}`}
      InputProps={{
        startAdornment: (
          <div className={classes.inputItems}>
            <SearchSmIcon className={classes.searchIcon} />
            {areManyProperties ? (
              <div className={classes.dropdown}>
                <OLButton
                  className={classes.button}
                  onClick={contextMenu.handleMenuOpen}
                  data-test-id="search-column-type-button"
                >
                  {message({ id: selectedProperty?.label })}
                  <ChevronDownIcon className={classes.dropdownIcon} />
                </OLButton>
                <Menu
                  anchorEl={contextMenu.anchor}
                  anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  onClose={contextMenu.handleMenuClose}
                  open={contextMenu.isMenuOpen}
                  transformOrigin={{ horizontal: "right", vertical: "top" }}
                  className={classes.menu}
                >
                  {properties?.map((item, index: number) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        contextMenu.handleMenuClose();
                        setSelectedProperty(item);
                        setInputValue("");
                      }}
                      data-test-id={`search-column-type-${kebabCase(item.label)}`}
                    >
                      {message({ id: item.label })}
                    </MenuItem>
                  ))}
                </Menu>
              </div>
            ) : null}
          </div>
        ),
        endAdornment: inputValue ? (
          <CloseIcon
            className={classes.closeIcon}
            onClick={() => setInputValue("")}
          />
        ) : null,
      }}
      name="value"
      onChange={(e) => {
        setInputValue(e.target.value);
      }}
      value={inputValue}
      placeholder={message({ id: "searchInTable" })}
      inputProps={{
        "data-test-id": "table-search",
        "data-hj-suppress": true,
        autoComplete: "off",
      }}
      size="small"
    />
  );
}
